import React, { useMemo } from "react";
import "./AnnotateBoxStatsPanel.css";

const boxToColony = (box) => {
  const x1 = Math.round(box.x);
  const y1 = Math.round(box.y);
  const x2 = Math.round(box.x + box.width);
  const y2 = Math.round(box.y + box.height);
  return [x1, y1, x2, y2];
};

const normalizeColony = (c) => (
  Array.isArray(c) && c.length === 4
    ? c.map((n) => Math.round(Number(n)))
    : c
);

const labelKey = (label) => (label == null ? "__null__" : String(label));

const colonyKey = (col) => {
  const c = normalizeColony(col);
  if (!Array.isArray(c) || c.length !== 4) return "";
  return c.join(",");
};

const toItemKey = (col, label) => `${colonyKey(col)}|${labelKey(label)}`;

const toMultiSet = (items) => {
  const map = new Map();
  items.forEach((key) => {
    if (!key) return;
    map.set(key, (map.get(key) || 0) + 1);
  });
  return map;
};

const sumPositiveDiff = (a, b) => {
  // sum(max(0, a[k] - b[k]))
  let sum = 0;
  a.forEach((countA, key) => {
    const countB = b.get(key) || 0;
    if (countA > countB) sum += (countA - countB);
  });
  return sum;
};

const getChangeSummary = (originalList, originalLabels, boxes) => {
  const origB = (originalList || []).map(normalizeColony);
  const origL = originalLabels || [];
  const curB = (Array.isArray(boxes) ? boxes : []).map(boxToColony);
  const curL = (Array.isArray(boxes) ? boxes : []).map((b) => b.customLabel);

  const originalItems = origB.map((col, i) => toItemKey(col, origL[i]));
  const currentItems = curB.map((col, i) => toItemKey(col, curL[i]));

  const originalSet = toMultiSet(originalItems);
  const currentSet = toMultiSet(currentItems);

  const rawAdded = sumPositiveDiff(currentSet, originalSet);
  const rawDeleted = sumPositiveDiff(originalSet, currentSet);

  // When order changes or boxes are edited, this appears as a delete+add pair.
  // Treat paired diffs as "Modified".
  const modified = Math.min(rawAdded, rawDeleted);
  const added = rawAdded - modified;
  const deleted = rawDeleted - modified;

  if (added === 0 && deleted === 0 && modified === 0) return null;
  return { added, deleted, modified };
};

const AnnotateBoxStatsPanel = ({
  boxes,
  colorOptions,
  originalColonyBoxList,
  originalLabels,
}) => {
  const stats = useMemo(() => {
    const total = Array.isArray(boxes) ? boxes.length : 0;

    const counts = (Array.isArray(boxes) ? boxes : []).reduce((acc, box) => {
      if (box?.customLabel === null || typeof box?.customLabel === "undefined") return acc;
      const key = String(box.customLabel);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const rows = (Array.isArray(colorOptions) ? colorOptions : []).map((opt) => ({
      key: opt.key,
      displayName: opt.displayName ?? opt.label,
      color: opt.color,
      count: counts[String(opt.value)] || 0,
    }));

    const changeSummary = getChangeSummary(
      originalColonyBoxList,
      originalLabels,
      boxes,
    );

    return { total, rows, changeSummary };
  }, [boxes, colorOptions, originalColonyBoxList, originalLabels]);

  return (
    <div className="AnnotateImageModal__StatsBox">
      <div className="AnnotateImageModal__StatsTitle">
        <span className="AnnotateImageModal__StatsTitleTotal">
          Total:
          <span className="AnnotateImageModal__StatsValue">{stats.total}</span>
        </span>
        {stats.changeSummary && (
          <span className="AnnotateImageModal__StatsChange">
            {stats.changeSummary.added > 0 && (
              <span className="AnnotateImageModal__StatsChangeItem">
                Added:
                <span className="AnnotateImageModal__StatsValue">{stats.changeSummary.added}</span>
              </span>
            )}
            {stats.changeSummary.deleted > 0 && (
              <span className="AnnotateImageModal__StatsChangeItem">
                Deleted:
                <span className="AnnotateImageModal__StatsValue">{stats.changeSummary.deleted}</span>
              </span>
            )}
            {stats.changeSummary.modified > 0 && (
              <span className="AnnotateImageModal__StatsChangeItem">
                Modified:
                <span className="AnnotateImageModal__StatsValue">{stats.changeSummary.modified}</span>
              </span>
            )}
          </span>
        )}
      </div>
      <div className="AnnotateImageModal__StatsRows">
        {stats.rows.map((row) => (
          <div key={row.key} className="AnnotateImageModal__StatsRow">
            <span className="AnnotateImageModal__StatsLabel">
              <span className="AnnotateImageModal__ColorDot" style={{ backgroundColor: row.color }} />
              {row.displayName}
            </span>
            <span className="AnnotateImageModal__StatsValue">{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnnotateBoxStatsPanel;
